// src/components/AddToHomeScreen.js
import React, { useState, useEffect } from 'react';

// ── Device detection ────────────────────────────────────────
function getDevice() {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
    if (/android/i.test(ua)) return 'android';
    return 'desktop';
}

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

// ── Inline SVG icons ────────────────────────────────────────
const ShareIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
);

const MenuIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary)" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="5" r="1.5" fill="var(--mom-primary)" />
        <circle cx="12" cy="12" r="1.5" fill="var(--mom-primary)" />
        <circle cx="12" cy="19" r="1.5" fill="var(--mom-primary)" />
    </svg>
);

const PhoneIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--mom-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="2" />
    </svg>
);

// ── Step component ──────────────────────────────────────────
function Step({ number, icon, title, description, tip }) {
    return (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '14px 0' }}>
            <div style={{
                width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {icon || (
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700, color: 'var(--mom-primary)' }}>{number}</span>
                )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 3, lineHeight: 1.4 }}>{title}</p>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', lineHeight: 1.55 }}>{description}</p>
                {tip && (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-primary)',
                        marginTop: 6, padding: '6px 10px', borderRadius: 8,
                        background: 'color-mix(in srgb, var(--mom-primary) 5%, transparent)',
                        lineHeight: 1.4,
                    }}>
                        {tip}
                    </p>
                )}
            </div>
        </div>
    );
}

// ── iOS Instructions ────────────────────────────────────────
function IOSSteps() {
    return (
        <>
            <Step number={1} icon={<ShareIcon />}
                title="Tap the Share button in Safari"
                description="Look for the square icon with an upward arrow at the bottom of your screen. It may be at the top if you have your address bar set to the top."
                tip="Important: This only works in Safari — not Chrome or other browsers on iPhone." />
            <Step number={2}
                title={'Scroll down and look for "Add to Home Screen"'}
                description={'A menu will slide up from the bottom. Scroll down through the list of options until you see "Add to Home Screen" with a + icon next to it. Tap on it.'}
                tip={'Don\u2019t see it? Scroll all the way to the bottom of that menu (or tap more), tap "Edit Actions," and toggle on "Add to Home Screen."'} />
            <Step number={3}
                title={'Turn on "Open as Web App"'}
                description={"You\u2019ll see a preview screen with the site name and icon. Before tapping \"Add,\" look for a toggle that says \"Open as Web App\" and make sure it\u2019s turned on. This will make it open full-screen like a real app."} />
            <Step number={4}
                title={'Tap "Add" in the top right'}
                description={"Tap the \"Add\" button in the top-right corner of your screen. The icon will now appear on your home screen and you can open it just like any other app."} />
        </>
    );
}

// ── Android Instructions ────────────────────────────────────
function AndroidSteps() {
    return (
        <>
            <Step number={1} icon={<MenuIcon />}
                title="Tap the three-dot menu in Chrome"
                description="Open this site in Chrome, then tap the three vertical dots in the top-right corner of the screen to open the browser menu."
                tip="Also works in Samsung Internet and Edge — the menu icon may look slightly different." />
            <Step number={2}
                title={'Look for "Add to Home screen" or "Install app"'}
                description={"Scroll through the menu options until you find \"Add to Home screen\" or \"Install app.\" It\u2019s usually about halfway down the list. Tap whichever one you see."} />
            <Step number={3}
                title="Confirm the name and tap Add"
                description={"A small popup will appear showing the site name and icon. You can edit the name if you\u2019d like. Tap \"Add\" to confirm. The icon will now appear on your home screen and you can open it just like any other app."} />
        </>
    );
}

// ════════════════════════════════════════════════════════════
//  ONE-TIME PROMPT — shows on first Dashboard visit (mobile only)
// ════════════════════════════════════════════════════════════
const STORAGE_KEY = 'mom_homescreen_prompt_dismissed';

export function HomeScreenPrompt() {
    const [visible, setVisible] = useState(false);
    const [device] = useState(getDevice);

    useEffect(() => {
        // Never show on desktop — and importantly, never write the
        // dismissed flag on desktop so mobile gets its own chance later
        if (device === 'desktop') return;
        // Don't show if already running as installed app
        if (isStandalone()) return;
        // Don't show if user already dismissed on a mobile device
        try {
            if (localStorage.getItem(STORAGE_KEY)) return;
        } catch (e) { return; }

        // Delay 2.5s so dashboard content loads first
        const timer = setTimeout(() => setVisible(true), 2500);
        return () => clearTimeout(timer);
    }, [device]);

    const dismiss = () => {
        setVisible(false);
        // Only write the flag when dismissing on mobile — this ensures
        // a desktop-first visit doesn't block the mobile prompt
        try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (e) { }
    };

    if (!visible) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9998,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            padding: '24px 16px',
        }}>
            {/* Backdrop */}
            <div onClick={dismiss} style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
            }} />

            {/* Bottom sheet */}
            <div style={{
                position: 'relative', zIndex: 1,
                width: '100%', maxWidth: 420,
                background: 'linear-gradient(180deg, #ffffff 0%, #fbfcfd 40%, #f6f8fa 100%)',
                borderRadius: 24,
                border: '1px solid rgba(255,255,255,0.5)',
                padding: '32px 28px 28px',
                boxShadow: '0 -4px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)',
                maxHeight: '80vh',
                overflowY: 'auto',
                animation: 'momSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
                {/* Close button */}
                <button onClick={dismiss} style={{
                    position: 'absolute', top: 14, right: 18,
                    background: 'none', border: 'none', fontSize: 22,
                    color: 'var(--mom-warm-gray)', cursor: 'pointer', lineHeight: 1,
                }} aria-label="Close">×</button>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
                        background: 'color-mix(in srgb, var(--mom-primary) 10%, transparent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <PhoneIcon />
                    </div>
                    <h3 style={{
                        fontFamily: 'var(--mom-font-serif)', fontSize: 20,
                        fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)',
                        marginBottom: 6,
                    }}>
                        Add to Home Screen
                    </h3>
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                        color: 'var(--mom-warm-gray)', lineHeight: 1.5,
                    }}>
                        Save this to your phone for quick, app-like access to your workouts.
                    </p>
                </div>

                {/* Device-specific steps */}
                <div style={{ borderTop: '1px solid var(--mom-cream-dark)', paddingTop: 4 }}>
                    {device === 'ios' ? <IOSSteps /> : <AndroidSteps />}
                </div>

                {/* Dismiss */}
                <button onClick={dismiss} style={{
                    width: '100%', marginTop: 16, padding: '14px',
                    borderRadius: 50, border: 'none', cursor: 'pointer',
                    background: 'var(--mom-primary)', color: 'var(--mom-white)',
                    fontFamily: 'var(--mom-font-sans)', fontSize: 15, fontWeight: 600,
                    boxShadow: '0 4px 16px color-mix(in srgb, var(--mom-primary) 25%, transparent)',
                    transition: 'all 0.15s ease',
                }}>
                    Got it
                </button>
            </div>

            <style>{`
                @keyframes momSlideUp {
                    from { transform: translateY(100%); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>
        </div>
    );
}

// ════════════════════════════════════════════════════════════
//  ACCOUNT CARD — permanent instructions in Account page
// ════════════════════════════════════════════════════════════
export function HomeScreenCard({ cardStyle, specHighlight }) {
    const [device] = useState(getDevice);
    const [activeTab, setActiveTab] = useState(device === 'android' ? 'android' : 'ios');
    const standalone = isStandalone();

    const tabStyle = (active) => ({
        flex: 1, padding: '8px 0', borderRadius: 10,
        border: 'none', cursor: 'pointer',
        fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
        background: active ? 'var(--mom-primary)' : 'transparent',
        color: active ? 'var(--mom-white)' : 'var(--mom-warm-gray)',
        transition: 'all 0.15s ease',
    });

    return (
        <div style={{ ...cardStyle, padding: '28px 28px' }}>
            {specHighlight && <div style={specHighlight} />}
            <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <PhoneIcon />
                    <div>
                        <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic', color: 'var(--mom-charcoal)' }}>
                            Add to Home Screen
                        </h2>
                        <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-warm-gray)', lineHeight: 1.4 }}>
                            {standalone ? "You're already using the app from your home screen!" : 'Save this site to your phone for quick, app-like access.'}
                        </p>
                    </div>
                </div>

                {!standalone && (
                    <>
                        {/* iOS / Android tabs */}
                        <div style={{
                            display: 'flex', gap: 4, padding: 4, borderRadius: 12,
                            background: 'var(--mom-cream)', marginBottom: 16,
                        }}>
                            <button onClick={() => setActiveTab('ios')} style={tabStyle(activeTab === 'ios')}>iPhone</button>
                            <button onClick={() => setActiveTab('android')} style={tabStyle(activeTab === 'android')}>Android</button>
                        </div>

                        <div>
                            {activeTab === 'ios' ? <IOSSteps /> : <AndroidSteps />}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}